import {
  createAuth0Client,
  type Auth0Client,
  type Auth0ClientOptions,
  type User,
} from "@auth0/auth0-spa-js";
import { identify } from "~/parent/analytics";

export type Session = {
  client: Auth0Client;
  user: User;
  getToken: () => Promise<string>;
};

export function auth0Options() {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
  const scope = import.meta.env.VITE_AUTH0_SCOPE;
  if (!domain || !clientId || !audience || !scope) {
    throw new Error("Missing Auth0 environment variables. See README.");
  }
  return {
    domain,
    clientId,
    cacheLocation: "localstorage",
    authorizationParams: {
      scope,
      audience,
      redirect_uri: window.location.origin,
    },
  } satisfies Auth0ClientOptions;
}

// Each caller sends the token as a bearer token to the api, so a missing token is an error.
async function requireToken(client: Auth0Client): Promise<string> {
  const token = await client.getTokenSilently();
  if (token === undefined) {
    throw new Error("Auth0 returned no access token");
  }
  return token;
}

export type StartAuthOptions = {
  /**
   * Auth0 accepts a redirect URL only if it is the same as an allowed callback URL, query
   * included. With this option, the login comes back to the path of the page with no query, and
   * Auth0 keeps the query in `appState`. Then only the path must be an allowed callback URL. Use
   * it for a page that reads its query, such as /oauth.
   */
  queryInAppState?: boolean;
};

type AppState = { search?: string };

/**
 * Returns the Auth0 session of the user.
 *
 * When the URL holds the `code` and `state` parameters of an Auth0 redirect, this function
 * completes the login and removes the two parameters from the URL. When no session exists, it
 * sends the browser to the Auth0 login page, and the promise does not resolve.
 */
export async function startAuth(options: StartAuthOptions = {}): Promise<Session> {
  const client = await createAuth0Client(auth0Options());

  const params = new URLSearchParams(window.location.search);
  if (params.has("code") && params.has("state")) {
    // A failed callback, for example with an old `state`, goes on to a new login below.
    let appState: AppState | undefined;
    const completed = await client.handleRedirectCallback<AppState>().then(
      (result) => {
        appState = result.appState;
        return true;
      },
      (error) => {
        console.error("Auth0 could not complete the login redirect:", error);
        return false;
      },
    );
    if (completed) {
      const user = await client.getUser();
      if (!user) {
        const Sentry = await import("@sentry/browser");
        Sentry.captureException("Unexpected missing Auth0 user.");
        window.location.href = "/";
        return new Promise<never>(() => {});
      }
      if (user.sub) identify(user.sub);
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("state");
    url.searchParams.delete("code");
    // A login with `queryInAppState` puts the query of the page back.
    if (typeof appState?.search === "string") {
      url.search = appState.search;
    }
    window.history.replaceState({}, "", url.toString());
  }

  const user = (await client.isAuthenticated())
    ? await client.getUser()
    : undefined;
  if (!user) {
    await client.loginWithRedirect<AppState>(
      options.queryInAppState
        ? {
            appState: { search: window.location.search },
            authorizationParams: {
              screen_hint: "login",
              redirect_uri: window.location.origin + window.location.pathname,
            },
          }
        : {
            authorizationParams: {
              screen_hint: "login",
              redirect_uri: window.location.href,
            },
          },
    );
    return new Promise<never>(() => {});
  }

  return { client, user, getToken: () => requireToken(client) };
}
